import { Check, ArrowUpRight, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { usePricingPlans } from "@/hooks/usePricingPlans";
import { APP_URL } from "@/lib/urls";
import {
  buildPlanFeatureLines,
  displayPrice,
  formatMoney,
  isHighlightedPlan,
  parseBadges,
} from "@/lib/pricing";

export default function PricingCards() {
  const { data: plans = [], isLoading, isError, refetch, isFetching } = usePricingPlans();

  return (
    <section data-testid="pricing-cards" className="relative py-16 sm:py-20 bg-secondary/30 dark:bg-secondary/20 border-y hairline">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl mb-10">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-muted-foreground mb-4">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-gradient" />
            Lab pricing
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05]">
            Plans that grow with your lab.
          </h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Monthly subscriptions only.
          </p>
        </div>

        {isLoading && (
          <div
            data-testid="pricing-loading"
            className="flex items-center justify-center gap-2 py-24 text-sm text-muted-foreground"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading lab plans…
          </div>
        )}

        {isError && !isLoading && (
          <div
            data-testid="pricing-error"
            className="rounded-3xl hairline glass p-10 text-center"
          >
            <p className="text-sm text-muted-foreground">
              We couldn&apos;t load pricing right now. Please try again.
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-foreground text-background text-sm font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Retry
            </button>
          </div>
        )}

        {!isLoading && !isError && plans.length === 0 && (
          <div
            data-testid="pricing-empty"
            className="rounded-3xl hairline glass p-10 text-center text-sm text-muted-foreground"
          >
            No active lab plans are available yet.
          </div>
        )}

        {!isLoading && !isError && plans.length > 0 && (
          <div
            className={`grid grid-cols-1 gap-4 ${
              plans.length === 1
                ? "md:grid-cols-1 max-w-md mx-auto"
                : plans.length === 2
                  ? "md:grid-cols-2 max-w-3xl mx-auto"
                  : "md:grid-cols-3"
            }`}
          >
            {plans.map((plan, i) => {
              const highlight = isHighlightedPlan(plan);
              const badges = parseBadges(plan.badge_label);
              const price = displayPrice(plan);
              const features = buildPlanFeatureLines(plan);
              const slug = String(plan.name || plan.id)
                .toLowerCase()
                .replace(/\s+/g, "-");

              return (
                <motion.div
                  key={plan.id}
                  data-testid={`pricing-tier-${slug}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  className={`relative rounded-3xl p-8 lg:p-10 overflow-hidden ${
                    highlight ? "bg-brand-ink text-white" : "hairline glass"
                  }`}
                  style={
                    !highlight && plan.preview_bg_color
                      ? { boxShadow: `inset 0 0 0 1px ${plan.preview_bg_color}33` }
                      : undefined
                  }
                >
                  {highlight && (
                    <div className="absolute inset-0 opacity-40 pointer-events-none">
                      <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-brand-gradient blur-3xl opacity-70" />
                    </div>
                  )}
                  <div className="relative">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-heading font-semibold text-xl">{plan.name}</div>
                      {badges[0] && (
                        <span
                          className={`text-[10px] tracking-[0.2em] uppercase font-semibold px-2.5 py-1 rounded-full shrink-0 ${
                            highlight
                              ? "bg-white/10 border border-white/15"
                              : "bg-black/5 dark:bg-white/10 border hairline"
                          }`}
                        >
                          {badges[0]}
                        </span>
                      )}
                    </div>
                    <div className={`text-xs mt-1 ${highlight ? "text-white/60" : "text-muted-foreground"}`}>
                      {plan.description || "For dental laboratories"}
                    </div>

                    <div className="mt-8">
                      {price.hasPrice ? (
                        <div className="flex items-baseline gap-2">
                          <span className="font-heading text-5xl lg:text-6xl font-semibold tracking-tighter">
                            {formatMoney(price.amount, price.currency)}
                          </span>
                          <span className={`text-xs ${highlight ? "text-white/60" : "text-muted-foreground"}`}>
                            {price.suffix}
                          </span>
                        </div>
                      ) : (
                        <div className="font-heading text-4xl lg:text-5xl font-semibold tracking-tighter">
                          Custom
                        </div>
                      )}
                      <div className={`text-[11px] mt-2 ${highlight ? "text-white/60" : "text-muted-foreground"}`}>
                        {price.note}
                      </div>
                    </div>

                    <ul className="mt-8 space-y-3">
                      {features.map((f) => (
                        <li key={f} className="flex items-start gap-2.5 text-sm">
                          <span
                            className={`h-4 w-4 rounded-full inline-flex items-center justify-center mt-0.5 ${
                              highlight ? "bg-white/10" : "bg-black/5 dark:bg-white/10"
                            }`}
                          >
                            <Check className="h-2.5 w-2.5" />
                          </span>
                          <span className={highlight ? "text-white/85" : ""}>{f}</span>
                        </li>
                      ))}
                    </ul>

                    {price.hasPrice ? (
                      <a
                        href={APP_URL}
                        data-testid={`pricing-cta-${slug}`}
                        className={`mt-10 group inline-flex items-center gap-2 w-full justify-center px-5 py-3 rounded-full font-semibold text-sm transition-opacity ${
                          highlight
                            ? "bg-white text-brand-ink hover:opacity-90"
                            : "bg-foreground text-background hover:opacity-90"
                        }`}
                      >
                        Get started
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </a>
                    ) : (
                      <Link
                        to="/contact"
                        data-testid={`pricing-cta-${slug}`}
                        className={`mt-10 group inline-flex items-center gap-2 w-full justify-center px-5 py-3 rounded-full font-semibold text-sm transition-opacity ${
                          highlight
                            ? "bg-white text-brand-ink hover:opacity-90"
                            : "bg-foreground text-background hover:opacity-90"
                        }`}
                      >
                        Talk to sales
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Link>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="mt-8 text-[11px] text-muted-foreground text-center">
          Lab plans only. Monthly billing in USD. Billing starts after activation. Applicable taxes may apply.
        </div>
      </div>
    </section>
  );
}
